
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Building2, Target, Calendar, Filter } from "lucide-react";
import { useProfilePrices } from "@/hooks/useProfilePrices";
import { useProfilePriceMatching } from "@/hooks/useProfilePriceMatching";
import { useUserFilters } from "@/hooks/useUserFilters";
import { PriceTableRow } from "./prices/PriceTableRow";
import { useState } from "react";

interface ProfilePrice {
  id: string;
  profile_report_id: string;
  commodity_name: string;
  variety: string | null;
  group_name: string | null;
  price: number;
  unit: string;
  market_type: string;
  report_date: string;
  market_city: string;
  grade: string | null;
  package_type: string | null;
  origin: string | null;
  created_at: string;
  updated_at: string;
}

export const ProfileTerminalPricesTable = () => {
  const { profilePrices, loading, fetchProfilePrices } = useProfilePrices();
  const { userCrops, userProfile, loading: userCropsLoading } = useUserFilters();
  const { matchAndFetchProfilePrices, loading: matchingLoading, error: matchingError } = useProfilePriceMatching();
  const [refreshing, setRefreshing] = useState(false);
  const [lastMatchResult, setLastMatchResult] = useState<any>(null);

  const handleEnhancedSmartRefresh = async () => {
    setRefreshing(true);
    try {
      console.log('🎯 Starting enhanced smart profile price matching...');
      const result = await matchAndFetchProfilePrices();
      console.log('✅ Enhanced smart matching completed:', result);
      
      setLastMatchResult(result);
      await fetchProfilePrices();
    } catch (err) {
      console.error('❌ Enhanced smart matching failed:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  // Group prices by commodity and variety for better table organization
  const groupedPrices = profilePrices.reduce((acc, price) => {
    const key = `${price.commodity_name}-${price.variety || 'No Variety'}`;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(price);
    return acc;
  }, {} as Record<string, ProfilePrice[]>);

  if (loading || userCropsLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-blue-800 flex items-center space-x-2">
            <Building2 size={20} />
            <span>Enhanced Terminal Market Prices (Table View)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold text-blue-800 flex items-center space-x-2">
            <Building2 size={20} />
            <span>Enhanced Terminal Market Prices</span>
            <Badge variant="secondary">{profilePrices.length}</Badge>
            <Badge className="bg-green-100 text-green-800 border-green-200 flex items-center space-x-1">
              <Calendar size={12} />
              <span>Last 4 Reports</span>
            </Badge>
            <Badge className="bg-purple-100 text-purple-800 border-purple-200 flex items-center space-x-1">
              <Filter size={12} />
              <span>Date Filtered</span>
            </Badge>
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Button 
              onClick={handleEnhancedSmartRefresh} 
              className="bg-green-600 hover:bg-green-700"
              disabled={refreshing || matchingLoading}
            >
              <Target size={14} className={`mr-2 ${refreshing || matchingLoading ? 'animate-spin' : ''}`} />
              {refreshing || matchingLoading ? 'Enhanced Matching...' : 'Enhanced Smart Match'}
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <p className="text-sm text-gray-600">
            Enhanced smart matching: Terminal Markets → Date Filter (Last Month) → Commodity Groups → Specific Varieties
          </p>
          {lastMatchResult && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center space-x-2 text-sm text-blue-800">
                <Calendar size={14} />
                <span className="font-medium">Last Match Results</span>
              </div>
              <p className="text-xs text-blue-700 mt-1">
                Found {lastMatchResult.matchedReports} matches from {lastMatchResult.dateRange ? 
                  `${formatDate(lastMatchResult.dateRange.from)} to ${formatDate(lastMatchResult.dateRange.to)}` : 
                  'recent reports'
                }. Generated {lastMatchResult.storedPrices} prices.
              </p>
            </div>
          )}
          {matchingError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <span className="text-red-600 text-sm">⚠️ {matchingError}</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {profilePrices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Building2 size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-medium mb-2">No enhanced profile prices available</p>
            <div className="space-y-2 text-sm">
              {userCrops.length === 0 ? (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-blue-800 font-medium mb-2">No Crops Configured</p>
                  <p className="text-blue-700">
                    Add crops to your preferences to get personalized terminal market prices.
                  </p>
                </div>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-yellow-800 font-medium mb-2">No Recent Prices Found</p>
                  <p className="text-yellow-700">
                    Use "Enhanced Smart Match" to find recent terminal market prices for your {userCrops.length} configured crops.
                  </p>
                  <div className="mt-2 text-xs text-yellow-600">
                    Location: {userProfile?.location || 'Not set'} | 
                    Crops: {userCrops.map(c => `${c.commodity}${c.variety ? `-${c.variety}` : ''}`).join(', ')}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-center mt-4">
              <Button 
                onClick={handleEnhancedSmartRefresh} 
                className="bg-green-600 hover:bg-green-700"
                disabled={refreshing || matchingLoading}
              >
                <Target size={16} className={`mr-2 ${refreshing || matchingLoading ? 'animate-spin' : ''}`} />
                {refreshing || matchingLoading ? 'Enhanced Matching...' : 'Enhanced Smart Match'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex items-center space-x-2 text-sm text-green-800">
                <Target size={14} />
                <span className="font-medium">Enhanced Smart-Matched Prices (Last 4 Reports)</span>
              </div>
              <p className="text-xs text-green-700 mt-1">
                These prices were intelligently matched using enhanced filtering: Recent Terminal Market reports → 
                Date filtering (last month) → Commodity groups → Specific varieties. 
                Based on your location: {userProfile?.location || 'Unknown'} and {userCrops.length} crop preferences.
              </p>
            </div>

            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Commodity & Variety</TableHead>
                    <TableHead>Price & Trend</TableHead>
                    <TableHead>Market Details</TableHead>
                    <TableHead>Grade & Package</TableHead>
                    <TableHead>Report Date</TableHead>
                    <TableHead>Origin</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {Object.entries(groupedPrices).map(([commodityKey, prices]) => (
                    prices.map((price, index) => (
                      <PriceTableRow 
                        key={price.id} 
                        price={price} 
                        isFirstInGroup={index === 0}
                      />
                    ))
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-800 mb-2">Enhanced Smart Profile-Based Terminal Market Pricing</h4>
              <p className="text-sm text-blue-700 mb-2">
                This enhanced system filters Terminal Market reports from the last month, then intelligently matches 
                commodity groups (Tree Fruits, Tree Nuts, etc.) with report names, and finally searches for your 
                specific commodities and varieties. Only the last 4 matching reports are shown.
              </p>
              <p className="text-xs text-blue-600">
                * Enhanced matching includes date filtering to ensure relevance and recency of price data.
                Results are displayed in an organized table format for better analysis and comparison.
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
